/**
 * Internal document management page — Google Drive-like folder navigation with upload/preview.
 */
import { Button, Field, icons } from '@apuestapr/ui';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';

import { useSessionContext } from '../auth/SessionProvider.tsx';
import { createDocumentsApi, type DocFile, type FolderContents } from './api.ts';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('es-PR', { day: 'numeric', month: 'short', year: 'numeric' });

const isPreviewable = (mimeType: string): boolean =>
  mimeType.startsWith('image/') || mimeType === 'application/pdf';

const FolderIcon = (): ReactElement => (
  <svg className="h-5 w-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
    <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
  </svg>
);

const FileIcon = ({ mimeType }: { readonly mimeType: string }): ReactElement => {
  if (mimeType.startsWith('image/')) {
    return <icons.image className="h-5 w-5 text-blue-500" />;
  }
  return <icons.fileText className="h-5 w-5 text-muted-foreground" />;
};

// ─── Breadcrumbs ─────────────────────────────────────────────────────────────

const Breadcrumbs = ({
  breadcrumbs,
  onNavigate,
}: {
  readonly breadcrumbs: readonly { readonly id: number; readonly name: string }[];
  readonly onNavigate: (folderId: number | null) => void;
}): ReactElement => (
  <nav aria-label="Breadcrumbs" className="flex items-center gap-1 text-sm">
    <button
      type="button"
      onClick={() => onNavigate(null)}
      className="rounded px-2 py-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
    >
      Documentos
    </button>
    {breadcrumbs.map((crumb) => (
      <span key={crumb.id} className="flex items-center gap-1">
        <span className="text-muted-foreground">/</span>
        <button
          type="button"
          onClick={() => onNavigate(crumb.id)}
          className="rounded px-2 py-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          {crumb.name}
        </button>
      </span>
    ))}
  </nav>
);

// ─── New Folder Dialog ───────────────────────────────────────────────────────

const NewFolderForm = ({
  onSubmit,
  onCancel,
}: {
  readonly onSubmit: (name: string) => void;
  readonly onCancel: () => void;
}): ReactElement => {
  const [name, setName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (name.trim()) onSubmit(name.trim());
  };

  return (
    <form onSubmit={handleSubmit} className="flex items-end gap-2">
      <Field label="Nombre de la carpeta">
        {(control) => (
          <input
            {...control}
            ref={inputRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nueva carpeta"
            className="flex h-9 w-full rounded-md border border-border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        )}
      </Field>
      <Button type="submit" size="sm" disabled={!name.trim()}>
        Crear
      </Button>
      <Button type="button" variant="outline" size="sm" onClick={onCancel}>
        Cancelar
      </Button>
    </form>
  );
};

// ─── Rename Inline ───────────────────────────────────────────────────────────

const RenameInput = ({
  currentName,
  onSubmit,
  onCancel,
}: {
  readonly currentName: string;
  readonly onSubmit: (name: string) => void;
  readonly onCancel: () => void;
}): ReactElement => {
  const [name, setName] = useState(currentName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (name.trim() && name.trim() !== currentName) onSubmit(name.trim());
    else onCancel();
  };

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-1">
      <input
        ref={inputRef}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={onCancel}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
        }}
        className="rounded border border-border bg-card px-2 py-0.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
      />
    </form>
  );
};

// ─── Preview Panel ───────────────────────────────────────────────────────────

const PreviewPanel = ({
  file,
  downloadUrl,
  onClose,
}: {
  readonly file: DocFile;
  readonly downloadUrl: string;
  readonly onClose: () => void;
}): ReactElement => (
  <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col border-l border-border bg-card shadow-overlay">
    <div className="flex items-center justify-between border-b border-border px-4 py-3">
      <h3 className="font-display text-sm font-bold truncate">{file.name}</h3>
      <button
        type="button"
        onClick={onClose}
        className="rounded p-1 hover:bg-muted transition-colors"
        aria-label="Cerrar preview"
      >
        <span className="text-lg leading-none">&times;</span>
      </button>
    </div>

    <div className="flex-1 overflow-auto p-4">
      {file.mime_type.startsWith('image/') ? (
        <img
          src={downloadUrl}
          alt={file.name}
          className="max-w-full rounded-lg border border-border"
        />
      ) : file.mime_type === 'application/pdf' ? (
        <iframe
          src={downloadUrl}
          title={file.name}
          className="h-full w-full min-h-[600px] rounded-lg border border-border"
        />
      ) : (
        <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
          <FileIcon mimeType={file.mime_type} />
          <p className="text-sm text-muted-foreground">
            Vista previa no disponible para este tipo de archivo.
          </p>
        </div>
      )}
    </div>

    <div className="border-t border-border px-4 py-3">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {formatBytes(file.size_bytes)} &middot; {file.mime_type}
        </span>
        <span>{formatDate(file.created_at)}</span>
      </div>
      <div className="mt-2">
        <a
          href={downloadUrl}
          download={file.name}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <icons.externalLink className="h-4 w-4" />
          Descargar
        </a>
      </div>
    </div>
  </div>
);

// ─── Main Page ───────────────────────────────────────────────────────────────

export const DocumentsPage = (): ReactElement => {
  const session = useSessionContext();
  const authenticated = session.state.status === 'authenticated' ? session.state.session : null;
  const csrfToken = authenticated?.csrfToken ?? '';
  const api = useMemo(() => createDocumentsApi(session.client), [session.client]);

  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
  const [contents, setContents] = useState<FolderContents | null>(null);
  const [loading, setLoading] = useState(true);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState<number | null>(null);
  const [renamingFile, setRenamingFile] = useState<number | null>(null);
  const [previewFile, setPreviewFile] = useState<DocFile | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async (): Promise<void> => {
      setLoading(true);
      try {
        const data = await api.listFolder(currentFolderId);
        if (!cancelled) {
          setContents(data);
        }
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [api, currentFolderId, refreshKey]);

  const loadFolder = useCallback((folderId: number | null) => {
    setCurrentFolderId(folderId);
  }, []);

  const reloadCurrentFolder = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  const handleNavigate = useCallback(
    (folderId: number | null) => {
      void loadFolder(folderId);
      setShowNewFolder(false);
      setRenamingFolder(null);
      setRenamingFile(null);
      setPreviewFile(null);
    },
    [loadFolder],
  );

  const handleCreateFolder = useCallback(
    async (name: string) => {
      try {
        await api.createFolder(name, currentFolderId, csrfToken);
        setShowNewFolder(false);
        reloadCurrentFolder();
      } catch {
        /* silent */
      }
    },

    [api, csrfToken, currentFolderId, reloadCurrentFolder],
  );

  const handleRenameFolder = useCallback(
    async (id: number, name: string) => {
      try {
        await api.renameFolder(id, name, csrfToken);
        setRenamingFolder(null);
        reloadCurrentFolder();
      } catch {
        /* silent */
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, csrfToken, currentFolderId, reloadCurrentFolder],
  );

  const handleDeleteFolder = useCallback(
    async (id: number, folderName: string) => {
      if (!confirm(`Eliminar carpeta "${folderName}" y todo su contenido?`)) return;
      try {
        await api.deleteFolder(id, csrfToken);
        reloadCurrentFolder();
      } catch {
        /* silent */
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, csrfToken, currentFolderId, reloadCurrentFolder],
  );

  const handleUpload = useCallback(
    async (files: FileList) => {
      setUploading(true);
      try {
        for (const file of files) {
          await api.uploadFile(file, currentFolderId, csrfToken);
        }
        reloadCurrentFolder();
      } catch {
        /* silent */
      } finally {
        setUploading(false);
      }
    },

    [api, csrfToken, currentFolderId, reloadCurrentFolder],
  );

  const handleRenameFile = useCallback(
    async (id: number, name: string) => {
      try {
        await api.renameFile(id, name, csrfToken);
        setRenamingFile(null);
        reloadCurrentFolder();
      } catch {
        /* silent */
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, csrfToken, currentFolderId, reloadCurrentFolder],
  );

  const handleDeleteFile = useCallback(
    async (id: number, fileName: string) => {
      if (!confirm(`Eliminar archivo "${fileName}"?`)) return;
      try {
        await api.deleteFile(id, csrfToken);
        reloadCurrentFolder();
        if (previewFile?.id === id) setPreviewFile(null);
      } catch {
        /* silent */
      }
    },
    [api, csrfToken, previewFile, reloadCurrentFolder],
  );

  if (!authenticated) return <></>;

  return (
    <section aria-labelledby="documents-heading" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 id="documents-heading" className="font-display text-2xl font-bold">
            Documentos
          </h1>
          <p className="text-muted-foreground">
            Archivos internos: contratos, compliance, facturas y mas.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowNewFolder(true)}
            disabled={showNewFolder}
          >
            <FolderIcon /> Nueva Carpeta
          </Button>
          <Button
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            loading={uploading}
            loadingLabel="Subiendo..."
          >
            <icons.plus className="h-4 w-4" /> Subir Archivo
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) void handleUpload(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      {/* Breadcrumbs */}
      {contents !== null && contents.breadcrumbs.length > 0 ? (
        <Breadcrumbs breadcrumbs={contents.breadcrumbs} onNavigate={handleNavigate} />
      ) : null}

      {/* New folder form */}
      {showNewFolder ? (
        <div className="rounded-xl border border-border bg-card p-4">
          <NewFolderForm
            onSubmit={(name) => void handleCreateFolder(name)}
            onCancel={() => setShowNewFolder(false)}
          />
        </div>
      ) : null}

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
        </div>
      ) : contents !== null ? (
        <div className="space-y-2">
          {/* Empty state */}
          {contents.folders.length === 0 && contents.files.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <FolderIcon />
              <p className="text-muted-foreground">Esta carpeta esta vacia.</p>
              <p className="text-sm text-muted-foreground">
                Crea una carpeta o sube un archivo para comenzar.
              </p>
            </div>
          ) : null}

          {/* Folders */}
          {contents.folders.map((folder) => (
            <div
              key={`folder-${String(folder.id)}`}
              className="group flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 hover:bg-muted/50 transition-colors"
            >
              <FolderIcon />
              {renamingFolder === folder.id ? (
                <RenameInput
                  currentName={folder.name}
                  onSubmit={(name) => void handleRenameFolder(folder.id, name)}
                  onCancel={() => setRenamingFolder(null)}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => handleNavigate(folder.id)}
                  className="flex-1 text-left font-medium text-sm hover:text-primary transition-colors"
                >
                  {folder.name}
                </button>
              )}
              <span className="text-xs text-muted-foreground">{formatDate(folder.created_at)}</span>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={() => setRenamingFolder(folder.id)}
                  className="rounded p-1 hover:bg-muted text-muted-foreground hover:text-foreground"
                  aria-label="Renombrar carpeta"
                >
                  <icons.pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => void handleDeleteFolder(folder.id, folder.name)}
                  className="rounded p-1 hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                  aria-label="Eliminar carpeta"
                >
                  <icons.trash className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}

          {/* Files */}
          {contents.files.map((file) => (
            <div
              key={`file-${String(file.id)}`}
              className="group flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 hover:bg-muted/50 transition-colors"
            >
              <FileIcon mimeType={file.mime_type} />
              {renamingFile === file.id ? (
                <RenameInput
                  currentName={file.name}
                  onSubmit={(name) => void handleRenameFile(file.id, name)}
                  onCancel={() => setRenamingFile(null)}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (isPreviewable(file.mime_type)) setPreviewFile(file);
                    else window.open(api.getDownloadUrl(file.id), '_blank');
                  }}
                  className="flex-1 text-left text-sm hover:text-primary transition-colors"
                >
                  {file.name}
                </button>
              )}
              <span className="text-xs text-muted-foreground">{formatBytes(file.size_bytes)}</span>
              <span className="text-xs text-muted-foreground">{formatDate(file.created_at)}</span>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {isPreviewable(file.mime_type) ? (
                  <button
                    type="button"
                    onClick={() => setPreviewFile(file)}
                    className="rounded p-1 hover:bg-muted text-muted-foreground hover:text-foreground"
                    aria-label="Ver preview"
                  >
                    <icons.eye className="h-4 w-4" />
                  </button>
                ) : null}
                <a
                  href={api.getDownloadUrl(file.id)}
                  download={file.name}
                  className="rounded p-1 hover:bg-muted text-muted-foreground hover:text-foreground"
                  aria-label="Descargar"
                >
                  <icons.externalLink className="h-4 w-4" />
                </a>
                <button
                  type="button"
                  onClick={() => setRenamingFile(file.id)}
                  className="rounded p-1 hover:bg-muted text-muted-foreground hover:text-foreground"
                  aria-label="Renombrar archivo"
                >
                  <icons.pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => void handleDeleteFile(file.id, file.name)}
                  className="rounded p-1 hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                  aria-label="Eliminar archivo"
                >
                  <icons.trash className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Preview panel */}
      {previewFile !== null ? (
        <PreviewPanel
          file={previewFile}
          downloadUrl={api.getDownloadUrl(previewFile.id)}
          onClose={() => setPreviewFile(null)}
        />
      ) : null}
    </section>
  );
};
